import { formatISO } from 'date-fns';
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { RangeDate, RgCraV2RiskHistoryOrderBy } from '@/globalTypes';
import { useQueryParams } from '@/hooks';
import { PlayerRgRiskAssessmentHistoryLegacyQuery } from './__generated__/usePlayerRgRiskAssessmentHistoryLegacy';
import { queryParams } from './queryParams';

const QUERY = gql`
  query PlayerRgRiskAssessmentHistoryLegacy(
    $rgCraEventId: String
    $playerGlobalId: ID
    $eventType: RgCraV2EventType
    $rgCraOutcomes: [RgCraV2OutcomeEnum!]
    $comment: String
    $initiatorId: String
    $initiatorType: InitiatorTypeEnum
    $rgCraProcessedAt: RangeDate
    $orderBy: RgCraV2RiskHistoryOrderBy
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    viewer {
      id
      rgRiskAssessmentHistory: rgCraV2LegacyRiskAssessmentHistory(
        rgCraEventId: $rgCraEventId
        playerGlobalId: $playerGlobalId
        rgCraEventType: $eventType
        rgCraOutcomes: $rgCraOutcomes
        comment: $comment
        initiatorId: $initiatorId
        initiatorType: $initiatorType
        rgCraProcessedAt: $rgCraProcessedAt
        rgCraHistoryOrderBy: $orderBy
        desc: $desc
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
        edges {
          node {
            eventId
            playerGlobalId
            playerId
            eventType
            comment
            initiator {
              ... on PlayerInitiator {
                player {
                  firstName
                }
              }
              ... on AgentInitiator {
                agent {
                  agentId
                  email
                  firstName
                  lastName
                }
              }
            }
            outcomes {
              name
              comment
              ... on RgCraV2RiskLevelUpdated {
                explanation
              }
              __typename
            }
            processedAt
          }
        }
      }
    }
  }
`;

export default function usePlayerRgRiskAssessmentHistoryLegacy(
  playerGlobalId: string,
  { paused }: { paused?: boolean } = {},
) {
  const [query, setQuery] = useQueryParams(queryParams);

  const rgCraProcessedAt = useMemo((): RangeDate | undefined => {
    if (query.legacyFrom && query.legacyTo) {
      return {
        from: formatISO(query.legacyFrom),
        to: formatISO(query.legacyTo),
      };
    }

    return undefined;
  }, [query.legacyFrom, query.legacyTo]);

  const [{ data, fetching }, refresh] =
    useQuery<PlayerRgRiskAssessmentHistoryLegacyQuery>({
      query: QUERY,
      variables: {
        playerGlobalId,
        after: query.legacyAfter,
        before: query.legacyBefore,
        desc: query.legacyDesc,
        eventType: query.legacyEventType,
        first: query.legacyFirst,
        last: query.legacyLast,
        orderBy: query.legacyOrderBy,
        rgCraProcessedAt,
      },
      pause: paused || !playerGlobalId,
    });

  const pageInfo = data?.viewer.rgRiskAssessmentHistory.pageInfo;

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    orderBy: query.legacyOrderBy,
    desc: query.legacyDesc,
    // `setQuery` defaults to `pushIn`, which merges into the existing params,
    // so these only need to pass the keys they actually change.
    setOrderBy: (order: RgCraV2RiskHistoryOrderBy) =>
      order === query.legacyOrderBy
        ? setQuery({ legacyDesc: !query.legacyDesc })
        : setQuery({ legacyOrderBy: order, legacyDesc: false }),
    nextPage: pageInfo?.hasNextPage
      ? () =>
          setQuery({
            legacyAfter: pageInfo.endCursor || undefined,
            legacyBefore: undefined,
          })
      : null,
    previousPage: pageInfo?.hasPreviousPage
      ? () =>
          setQuery({
            legacyAfter: undefined,
            legacyBefore: pageInfo.startCursor || undefined,
          })
      : null,
    history: data?.viewer.rgRiskAssessmentHistory?.edges?.map(
      (edge) => edge?.node,
    ),
  };
}
